/*
 * Seed wording for a blank recommendation, shared by the internal recommenders
 * on the review screen and the external recommender's public page so both are
 * handed the same starting draft.
 */
import type {IntlShape} from 'react-intl'

/** Marks where the author's own writing goes in a seeded recommendation. */
export const bodyPlaceholder = (intl: IntlShape) =>
  intl.formatMessage({id: 'COUNSELLING.LETTER.BODY_PLACEHOLDER'})

/** The editor emits markup even when empty, so strip tags before testing. */
export const hasEditorContent = (value: string) => !!value.replace(/<[^>]*>/g, '').trim()

/**
 * First candidate that is real text.
 *
 * View models fill unknown fields with an em dash for display, so a plain
 * `a || b` chain accepts "—" as a name and never reaches its fallback.
 */
export const firstMeaningful = (...values: (string | null | undefined)[]) =>
  values.find((v) => {
    const text = String(v ?? '').trim()
    return text !== '' && text !== '—' && text !== '-' && text !== 'N/A'
  }) ?? ''

/**
 * Seed wording for a blank recommendation.
 *
 * The salutation and sign-off used to be fixed chrome printed around the
 * editor. They are now written into it as an ordinary starting draft: every
 * word is editable, and deleting them is allowed.
 */
export const buildRecommendationPrefill = ({
  intl,
  studentName,
  authorName,
  authorTitle,
}: {
  intl: IntlShape
  studentName: string
  authorName: string
  authorTitle: string
}) =>
  [
    `<p><b>${intl.formatMessage({id: 'COUNSELLING.LETTER.SALUTATION'})}</b></p>`,
    `<p>${intl.formatMessage(
      {id: 'COUNSELLING.LETTER.OPENING'},
      {student: `<b>${studentName}</b>`}
    )}</p>`,
    `<p><br></p>`,
    // Marks where the author's own writing goes. Without it the seed reads as a
    // finished one-line letter and it is not obvious the body is theirs to
    // fill in. Submit is blocked while this line is still present.
    `<p>${bodyPlaceholder(intl)}</p>`,
    `<p><br></p>`,
    `<p>${intl.formatMessage({id: 'COUNSELLING.LETTER.SIGN_OFF'})}</p>`,
    // An unresolved name falls back to the bare role, which reads badly above
    // the title line ("Teacher / Physics Teacher"). Print the title alone in
    // that case — the author fills in their name over it either way.
    !authorName.trim() || authorTitle.trim().toLowerCase().endsWith(authorName.trim().toLowerCase())
      ? `<p><b>${authorTitle}</b></p>`
      : `<p><b>${authorName}</b><br>${authorTitle}</p>`,
  ].join('')

/** Visible text of an editor value, for comparisons that ignore markup. */
export const visibleText = (html: string) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

/**
 * True when the body placeholder is still somewhere in the letter.
 *
 * Checked independently of isUntouchedPrefill: an author may write a real
 * paragraph yet leave the placeholder line behind, which would otherwise print
 * verbatim in the PDF sent to an admissions office.
 */
export const hasBodyPlaceholder = (value: string, intl: IntlShape) =>
  visibleText(value).includes(visibleText(bodyPlaceholder(intl)))

/**
 * Submit error for a letter that still holds the placeholder line. Names the
 * line outright: authors who wrote their letter around it read the generic
 * "please write the recommendation" as a bug, not as something left to delete.
 */
export const bodyPlaceholderError = (intl: IntlShape) =>
  intl.formatMessage(
    {id: 'COUNSELLING.LETTER.PLACEHOLDER_LEFT'},
    {placeholder: bodyPlaceholder(intl)}
  )

/**
 * True when the editor still holds nothing but the seed text we supplied.
 *
 * Compared on visible text, not markup: the editor rewrites whitespace and tag
 * structure as soon as the field is focused, so an HTML equality check would
 * report "edited" for a letter nobody typed into.
 */
export const isUntouchedPrefill = (value: string, seeded: string | null) => {
  if (!seeded) return false
  return visibleText(value) === visibleText(seeded)
}

const prefillOpening = (intl: IntlShape, studentName: string) =>
  visibleText(
    [
      intl.formatMessage({id: 'COUNSELLING.LETTER.SALUTATION'}),
      intl.formatMessage({id: 'COUNSELLING.LETTER.OPENING'}, {student: studentName}),
    ].join(' ')
  )

/**
 * True when a stored draft is still untouched boilerplate, whatever name it was
 * seeded with. Used to repair a stale signature rather than preserve it.
 *
 * Between the opening and the closing sits either the body placeholder (current
 * seed) or nothing at all (drafts seeded before it was added), so both shapes
 * are accepted — otherwise the older ones would stop being repairable.
 */
export const isUnwrittenBoilerplate = (stored: string, studentName: string, intl: IntlShape) => {
  const text = visibleText(stored)
  if (!text) return false

  const opening = prefillOpening(intl, studentName)
  if (!text.startsWith(opening)) return false

  const rest = text.slice(opening.length).trim()
  const signOff = visibleText(intl.formatMessage({id: 'COUNSELLING.LETTER.SIGN_OFF'}))
  const closingAt = rest.indexOf(signOff)
  if (closingAt === -1) return false

  // Anything between the opening and the closing must be the placeholder or
  // empty; a real paragraph there means the author has written something.
  const middle = rest.slice(0, closingAt).trim()
  if (middle && middle !== visibleText(bodyPlaceholder(intl))) return false

  // What follows the closing is the seed's signature block; longer than a name
  // and title could plausibly be means the author wrote it themselves.
  const afterClosing = rest.slice(closingAt + signOff.length).trim()
  return afterClosing.length <= 80
}
